import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, toggleButtonClasses } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '@/state/workflow.state';
import { color } from '@/colors';
import { log } from '@/logger/logger.ts';
import { Flex, FlexCenVer, FlexColCenHorVer } from '@/UI/styles';
import { LoadingCircle } from '@/UI/Animations/LoadingCircle';
import { AppToggleButtons } from '@/UI/AppToggleButtons/AppToggleButtons';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { I18N_KEYS } from '@/language/keys';
import { useMaskExtractorView, useNodeNullable } from '@/components/Recipe/FlowContext';
import NodeErrorOverlay from './Shared/NodeErrorOverlay';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { NodeViewer } from './Shared/FileViewer';
import type { MaskExtractorData } from '@/types/node';

const logger = log.getLogger('MaskExtractorNode');

interface MaskExtractorNodeProps {
  id: string;
  recipeId: string;
  recipeVersion: number;
  data: MaskExtractorData;
}

export function MaskExtractorNode({ id, data }: MaskExtractorNodeProps) {
  const { t } = useTranslation();
  const view = useMaskExtractorView(id);
  const node = useNodeNullable(id);
  const { locked } = node ?? {};
  const recipe = useWorkflowStore((state) => state.recipe);

  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const prevInput = useRef(data.image);
  // Handle state transitions
  useEffect(() => {
    if (!node) return;
    const prevInputValue = prevInput.current;

    async function handleStateTransition() {
      if (isLoading) return;

      setIsLoading(true);
      setHasError(false);

      try {
        await view.handleStateTransition(prevInputValue, recipe.id, recipe.version, (error) => {
          logger.error('Error running mask extraction.', error);
          setHasError(true);
        });
      } catch (error) {
        logger.error('Error running mask extraction.', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    }

    void handleStateTransition();

    prevInput.current = data.image;

    // FIXME: use isLoading only for UI - handle cancellation with AbortContoller
    // rerun on input or state change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.state?.type, data.image?.outputId, data.image?.nodeId]);

  const toggleDisplayMode = useCallback(() => view.toggleDisplayMode(), [view]);
  const recalculateMask = useCallback(() => view.recalculateMask(), [view]);

  return (
    <DynamicNode2 id={id} data={data}>
      <Box className="nodrag" sx={{ position: 'relative', pointerEvents: locked ? 'none' : 'auto' }}>
        <NodeViewer id={id} />
        {hasError && <NodeErrorOverlay errorMessage={t(I18N_KEYS.RECIPE_MAIN.NODES.MASKS.ERROR)} />}
        {isLoading && (
          <FlexColCenHorVer
            sx={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              backgroundColor: color.Black64_T,
              gap: 1,
            }}
          >
            <LoadingCircle size={24} color={color.White100} />
          </FlexColCenHorVer>
        )}
      </Box>

      {!hasError && data.state?.type === 'done' && (
        <Flex
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            backgroundColor: color.Black84,
            p: 0.5,
            borderRadius: 2,
          }}
        >
          <AppToggleButtons
            value={data.displayMode}
            options={[
              { value: 'image', label: t(I18N_KEYS.RECIPE_MAIN.NODES.MASKS.IMAGE) },
              { value: 'mask', label: t(I18N_KEYS.RECIPE_MAIN.NODES.MASKS.MASK) },
            ]}
            onChange={toggleDisplayMode}
            sx={{
              [`& .${toggleButtonClasses.root}`]: {
                '&:hover': {
                  background: color.White16_T,
                },
                [`&.${toggleButtonClasses.selected}`]: {
                  background: color.White80_T,
                  color: color.Black92,
                  '&:hover': {
                    background: color.White100,
                    color: color.Black92,
                  },
                },
              },
            }}
          />
        </Flex>
      )}

      <FlexCenVer sx={{ justifyContent: 'flex-end', mt: 1, mb: -1 }}>
        <ButtonContained size="small" mode="text" onClick={recalculateMask}>
          {t(I18N_KEYS.RECIPE_MAIN.NODES.MASKS.RECALCULATE_MASK)}
        </ButtonContained>
      </FlexCenVer>
    </DynamicNode2>
  );
}
